/* eslint-disable @typescript-eslint/no-explicit-any */
import * as Sentry from '@sentry/react'

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN
const ENVIRONMENT = import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development'
const TRACES_SAMPLE_RATE = parseFloat(
    import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || '1.0'
)
const PROFILES_SAMPLE_RATE = parseFloat(
    import.meta.env.VITE_SENTRY_PROFILES_SAMPLE_RATE || '1.0'
)

export function initSentry() {
    if (!SENTRY_DSN) {
        console.warn(
            'Sentry DSN not configured. Skipping Sentry initialization.'
        )
        return
    }

    Sentry.init({
        dsn: SENTRY_DSN,
        environment: ENVIRONMENT,
        integrations: [
            // Browser Tracing for performance monitoring
            Sentry.browserTracingIntegration(),
            // Browser Profiling for performance profiling
            Sentry.browserProfilingIntegration(),
            // Replay for session replays
            Sentry.replayIntegration({
                maskAllText: false,
                blockAllMedia: false,
            }),
            // Feedback for user feedback collection
            Sentry.feedbackIntegration({
                colorScheme: 'system',
                showBranding: false,
            }),
        ],

        // Performance Monitoring
        tracesSampleRate: TRACES_SAMPLE_RATE, // Capture 100% of transactions for tracing

        // Session Replay
        replaysSessionSampleRate: 0.1, // 10% of sessions will be recorded
        replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors will be recorded

        // Profiling
        profilesSampleRate: PROFILES_SAMPLE_RATE, // Profile 100% of transactions

        // Release tracking
        release: import.meta.env.VITE_APP_VERSION || '0.0.0',

        // Additional options
        beforeSend(event) {
            // Filter out non-error events in development
            if (ENVIRONMENT === 'development' && event.level === 'log') {
                return null
            }
            return event
        },

        // Ignore specific errors
        ignoreErrors: [
            // Browser extensions
            'top.GLOBALS',
            // Random plugins/extensions
            'originalCreateNotification',
            'canvas.contentDocument',
            'MyApp_RemoveAllHighlights',
            // Facebook borked
            'fb_xd_fragment',
            // ISP optimizing proxy - `Cache-Control: no-transform` seems to
            // reduce this. (thanks @acdha)
            'bmi_SafeAddOnload',
            'EBCallBackMessageReceived',
            // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
            'conduitPage',
        ],

        // Ignore specific URLs
        denyUrls: [
            // Chrome extensions
            /extensions\//i,
            /^chrome:\/\//i,
            /^chrome-extension:\/\//i,
            // Firefox extensions
            /^moz-extension:\/\//i,
        ],
    })

    console.log(`Sentry initialized in ${ENVIRONMENT} environment`)
}

// Helper function to set user context
export function setSentryUser(user: {
    id: string
    email?: string
    firstName?: string
    lastName?: string
}) {
    Sentry.setUser({
        id: user.id,
        email: user.email,
        username:
            user.firstName && user.lastName
                ? `${user.firstName} ${user.lastName}`
                : undefined,
    })
}

// Helper function to clear user context (on logout)
export function clearSentryUser() {
    Sentry.setUser(null)
}

// Helper function to capture custom errors
export function captureError(error: Error, context?: Record<string, any>) {
    if (context) {
        Sentry.setContext('additional_info', context)
    }
    Sentry.captureException(error)
}

// Helper function to capture custom messages
export function captureMessage(
    message: string,
    level: Sentry.SeverityLevel = 'info'
) {
    Sentry.captureMessage(message, level)
}

// Helper function to add breadcrumbs
export function addBreadcrumb(
    message: string,
    category?: string,
    data?: Record<string, any>
) {
    Sentry.addBreadcrumb({
        message,
        category: category || 'custom',
        data,
        level: 'info',
    })
}

export default Sentry
